import { Skeleton } from 'antd';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAssistant } from '@entities/assistant';
import {
  chatQueryKeys,
  useCreateThreadMutation,
  useMessages,
  useSendMessageMutation,
  useThreads,
  type Message,
} from '@entities/chat';
import { ModelAvatar, useModels, type LlmModel } from '@entities/llm';
import { CitationPills, MessageImages, Thinking } from '@features/test-chat';
import { extractErrorMessage } from '@shared/api';
import { Markdown } from '@shared/ui/Markdown';
import { useQueryClient } from '@tanstack/react-query';
import { ComparePanel } from './ComparePanel';
import './test-chat.css';

/**
 * Временная модель тест-чата действует только в этом диалоге (Р42): храним по id диалога
 * (localStorage — переживает перезагрузку и открывается в другой вкладке).
 */
const TMP_KEY = (threadId: string) => `richat.testModel.${threadId}`;
function readTmp(threadId?: string): number | null {
  if (!threadId) return null;
  try {
    const v = Number(window.localStorage.getItem(TMP_KEY(threadId)));
    return Number.isFinite(v) && v > 0 ? v : null;
  } catch {
    return null;
  }
}
function writeTmp(threadId: string, id: number | null) {
  try {
    if (id) window.localStorage.setItem(TMP_KEY(threadId), String(id));
    else window.localStorage.removeItem(TMP_KEY(threadId));
  } catch {
    /* ignore */
  }
}

const PANEL_KEY = 'richat.testChat.panel';

function SendIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 19V5M5 12l7-7 7 7" />
    </svg>
  );
}

function Answer({ m }: { m: Message }) {
  const images = m.images ?? [];
  const chunkIds = m.retrieved_chunk_ids ?? [];
  return (
    <div className="tc-answer">
      <div className="tc-answer-text tc-md">
        <Markdown text={m.content} />
      </div>
      {images.length > 0 && <MessageImages images={images} />}
      {chunkIds.length > 0 && <CitationPills chunkIds={chunkIds} />}
      {m.model_title && <div className="tc-model-caption">{m.model_title}</div>}
    </div>
  );
}

function Separator({ title, own }: { title: string; own: boolean }) {
  return (
    <div role="separator" className="tc-switch">
      <span />
      <b>
        Дальше отвечает {title} — {own ? 'модель ассистента' : 'только в тест-чате'}
      </b>
      <span />
    </div>
  );
}

/**
 * Экран 4 «Ассистент → Тест-чат» (утверждён 28.10.2026): чат на всю страницу, строка
 * сверху и поле ввода снизу всегда на месте; справа — «Сравнить с другой моделью».
 */
export function TestChatPage() {
  const { id = '' } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const { data: assistant } = useAssistant(id);
  const { data: models } = useModels();
  const { data: threads, isLoading: threadsLoading } = useThreads(id);
  const createThread = useCreateThreadMutation(id);
  const creating = useRef(false);
  const threadId = threads?.[0]?.id;

  useEffect(() => {
    if (!threadsLoading && threads && threads.length === 0 && !creating.current) {
      creating.current = true;
      createThread.mutate();
    }
  }, [threads, threadsLoading, createThread]);

  const { data: messages, isLoading: msgsLoading } = useMessages(threadId);
  const send = useSendMessageMutation(threadId ?? '');

  const [tmpId, setTmpId] = useState<number | null>(null);
  useEffect(() => setTmpId(readTmp(threadId)), [threadId]);

  const [panelOpen, setPanelOpen] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(PANEL_KEY) === '1';
    } catch {
      return false;
    }
  });
  const togglePanel = (v: boolean) => {
    setPanelOpen(v);
    try {
      window.localStorage.setItem(PANEL_KEY, v ? '1' : '0');
    } catch {
      /* ignore */
    }
  };

  const assistantModel = models?.find((m) => m.id === assistant?.model_id) ?? null;
  // Временная модель, которую сняли с каталога, больше не действует — отвечает модель ассистента.
  const testModel = tmpId ? (models?.find((m) => m.id === tmpId) ?? null) : null;
  useEffect(() => {
    if (threadId && tmpId && models && !testModel) {
      writeTmp(threadId, null);
      setTmpId(null);
    }
  }, [threadId, tmpId, models, testModel]);
  const current: LlmModel | null = testModel ?? assistantModel;

  // Поле ввода
  const [text, setText] = useState('');
  // Поле ввода растёт вверх под длину текста (потолок — max-height в CSS).
  const inputRef = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [text]);

  /**
   * Смена модели — новый диалог: иначе новая модель продолжала бы чужую переписку
   * и было бы непонятно, как она ответила бы на первые вопросы. Пустой диалог не
   * плодим — просто меняем модель в нём.
   */
  const switchModel = (next: number | null) => {
    if (!threadId || createThread.isPending) return;
    if ((messages?.length ?? 0) === 0) {
      writeTmp(threadId, next);
      setTmpId(next);
      return;
    }
    creating.current = true;
    send.reset();
    setText('');
    createThread.mutate(undefined, {
      onSuccess: (t) => {
        writeTmp(t.id, next);
        setTmpId(next);
      },
    });
  };
  const applyModel = (m: LlmModel) => switchModel(m.id === assistant?.model_id ? null : m.id);
  const resetModel = () => switchModel(null);

  const newDialog = () => {
    creating.current = true;
    send.reset();
    createThread.mutate(undefined, {
      onSuccess: () => qc.invalidateQueries({ queryKey: chatQueryKeys.threads(id) }),
    });
  };

  const doSend = () => {
    const t = text.trim();
    if (!t || !threadId || send.isPending) return;
    setText('');
    send.mutate(
      { content: t, model_id: testModel?.id },
      // Ошибка (нет денег, сбой) — вопрос возвращаем в поле, чтобы не набирать заново.
      { onError: () => setText((cur) => cur || t) },
    );
  };

  // Прокрутка вниз при новом сообщении
  const scroller = useRef<HTMLDivElement>(null);
  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: 'smooth' });
  }, [messages?.length, send.isPending]);

  const noMoney =
    (send.error as { response?: { status?: number } } | null)?.response?.status === 402;
  const sendError = send.error ? extractErrorMessage(send.error) : null;

  // Пунктирные черты «Дальше отвечает …» — по подписям ответов: где модель сменилась.
  const items = useMemo(() => messages ?? [], [messages]);
  const feed = useMemo(() => {
    const out: Array<{ kind: 'msg'; m: Message } | { kind: 'sep'; key: string; title: string }> =
      [];
    let prev: string | null = null;
    for (let i = 0; i < items.length; i += 1) {
      const m = items[i];
      if (m.role === 'user') {
        const answer = items[i + 1]?.role === 'assistant' ? items[i + 1] : null;
        const t = answer?.model_title ?? null;
        if (t && prev && t !== prev) out.push({ kind: 'sep', key: `sep-${m.id}`, title: t });
        if (t) prev = t;
      }
      out.push({ kind: 'msg', m });
    }
    return { out, lastTitle: prev };
  }, [items]);
  // Модель сменили, а ответа новой ещё не было — черта в конце переписки.
  const pendingSwitch =
    current && feed.lastTitle && current.title !== feed.lastTitle ? current.title : null;

  return (
    <div className="tc-wrap">
      <section className="tc-main" aria-label="Тест-чат">
        <div className="tc-top">
          <span className="tc-title">Тест-чат</span>
          <span aria-hidden="true" className="tc-sep" />
          {current && (
            <span className="tc-cur">
              <ModelAvatar model={current} size={22} />
              <span className="tc-cur-name" title={current.title}>
                {current.title}
              </span>
              {testModel ? (
                <span className="tc-only">только в тест-чате</span>
              ) : (
                <span className="v2-xs v2-muted tc-own">модель ассистента</span>
              )}
            </span>
          )}
          {testModel && assistantModel && (
            <button
              type="button"
              className="tc-btn"
              onClick={resetModel}
              title={`Вернуть ${assistantModel.title}`}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M9 14L4 9l5-5M4 9h11a5 5 0 0 1 0 10h-3" />
              </svg>
              Вернуть<span className="tc-wide"> {assistantModel.title}</span>
            </button>
          )}
          {!panelOpen && (
            <button
              type="button"
              className="tc-btn"
              onClick={() => togglePanel(true)}
              title="Сравнить с другой моделью"
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M7 7h13M16 3l4 4-4 4M17 17H4M8 13l-4 4 4 4" />
              </svg>
              Сравнить<span className="tc-wide"> с другой моделью</span>
            </button>
          )}
          <button
            type="button"
            className="tc-btn tc-new"
            onClick={newDialog}
            disabled={createThread.isPending}
            title="Новый диалог"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M12 5v14M5 12h14" />
            </svg>
            <span className="tc-mid">Новый диалог</span>
          </button>
        </div>

        <div className="tc-scroll" ref={scroller}>
          <div className="tc-feed">
            {(threadsLoading || msgsLoading) && <Skeleton active paragraph={{ rows: 4 }} />}
            {!msgsLoading && items.length === 0 && !send.isPending && (
              <div className="tc-empty">
                <div style={{ fontWeight: 600, color: '#1F2937' }}>Проверьте ассистента</div>
                <div>
                  Задайте вопрос так, как спросил бы ваш клиент. Ответы списываются по токенам, как
                  в каналах.
                </div>
              </div>
            )}
            {feed.out.map((x) =>
              x.kind === 'sep' ? (
                <Separator key={x.key} title={x.title} own={x.title === assistantModel?.title} />
              ) : x.m.role === 'user' ? (
                <div key={x.m.id} className="tc-q">
                  {x.m.content}
                </div>
              ) : (
                <Answer key={x.m.id} m={x.m} />
              ),
            )}
            {pendingSwitch && !send.isPending && (
              <Separator title={pendingSwitch} own={pendingSwitch === assistantModel?.title} />
            )}
            {send.isPending && <Thinking />}
            {sendError && (
              <div className={noMoney ? 'tc-alert is-money' : 'tc-alert'}>
                <span>
                  {noMoney ? 'Баланс исчерпан — пополните, чтобы продолжить проверку.' : sendError}
                </span>
                {noMoney && (
                  <Link to="/billing?topup=1" className="v2-btn is-primary">
                    Пополнить
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="tc-input-zone">
          <div className="tc-input-wrap">
            <div className="tc-input">
              <label htmlFor="msg" className="v2-sr">
                Сообщение
              </label>
              <textarea
                id="msg"
                ref={inputRef}
                rows={2}
                value={text}
                placeholder="Спросите так, как спросил бы ваш клиент…"
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    doSend();
                  }
                }}
              />
              <button
                type="button"
                className="tc-send"
                aria-label="Отправить"
                title="Отправить"
                onClick={doSend}
                disabled={!text.trim() || send.isPending || !threadId}
              >
                <SendIcon />
              </button>
            </div>
            <div className="tc-hint">Enter — отправить, Shift+Enter — новая строка</div>
          </div>
        </div>
      </section>

      {panelOpen && models && (
        <ComparePanel
          models={models}
          assistantModelId={assistant?.model_id ?? null}
          currentId={current?.id ?? null}
          onApply={applyModel}
          onClose={() => togglePanel(false)}
          catalogHref={`/assistants/${id}/settings/model/all`}
        />
      )}
    </div>
  );
}
